import { useMemo } from 'react';
import { EventBarChartProps } from '@v2/pages/common/components/NCFileBatchEvents/components/EventBarChart';
import { NcFileBatchEventType } from '@v2/pages/common/enums';
import { EventsPerspective } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { calculatePercentage } from '@helpers';

interface TimeValues
  extends Pick<
    EventBarChartProps,
    | 'activeTimeInSec'
    | 'changeTimeInSec'
    | 'setupTimeInSec'
    | 'idealTimeInSec'
    | 'paidTimeInSec'
    | 'actualTimeInSec'
    | 'scheduledTimeInSec'
    | 'maxTimeInSec'
    | 'eventType'
    | 'eventsPerspective'
  > {}

interface EventPercentages {
  activePercentage: number;
  changePercentage: number;
  setupPercentage: number;
  idealPercentage: number;
  paidPercentage: number;
  actualPercentage: number;
  scheduledPercentage: number;
}

export const useEventPercentages = ({
  eventType,
  eventsPerspective,
  activeTimeInSec = 0,
  changeTimeInSec = 0,
  setupTimeInSec = 0,
  idealTimeInSec = 0,
  paidTimeInSec = 0,
  actualTimeInSec = 0,
  scheduledTimeInSec = 0,
  maxTimeInSec = 0,
}: TimeValues): EventPercentages => {
  return useMemo(() => {
    const realActiveTimeInSec = activeTimeInSec;
    const realChangeTimeInSec = changeTimeInSec;
    let correctedActiveTimeForCycleMachineEvent = realActiveTimeInSec;
    let correctedChangeTimeForCycleMachineEvent = realChangeTimeInSec;

    const multiOrSingleMachinePerspective =
      eventsPerspective === EventsPerspective.SINGLE_MACHINE || eventsPerspective === EventsPerspective.MULTI_MACHINES;
    const cycleTimeGreaterThanMaxTime = realActiveTimeInSec + realChangeTimeInSec > maxTimeInSec;

    if (eventType === NcFileBatchEventType.CYCLE && multiOrSingleMachinePerspective && cycleTimeGreaterThanMaxTime) {
      const coefCorrectedActiveTimeForCycleMachineEvent = realActiveTimeInSec / (realActiveTimeInSec + realChangeTimeInSec);
      const coefCorrectedChangeTimeForCycleMachineEvent = realChangeTimeInSec / (realActiveTimeInSec + realChangeTimeInSec);
      correctedActiveTimeForCycleMachineEvent = maxTimeInSec * coefCorrectedActiveTimeForCycleMachineEvent;
      correctedChangeTimeForCycleMachineEvent = maxTimeInSec * coefCorrectedChangeTimeForCycleMachineEvent;
    }

    return {
      activePercentage: calculatePercentage(correctedActiveTimeForCycleMachineEvent, maxTimeInSec),
      changePercentage: calculatePercentage(correctedChangeTimeForCycleMachineEvent, maxTimeInSec),
      setupPercentage: calculatePercentage(setupTimeInSec, maxTimeInSec),
      idealPercentage: calculatePercentage(idealTimeInSec, maxTimeInSec),
      paidPercentage: calculatePercentage(paidTimeInSec, maxTimeInSec),
      actualPercentage: calculatePercentage(actualTimeInSec, maxTimeInSec),
      scheduledPercentage: calculatePercentage(scheduledTimeInSec, maxTimeInSec),
    };
  }, [
    activeTimeInSec,
    changeTimeInSec,
    setupTimeInSec,
    idealTimeInSec,
    paidTimeInSec,
    scheduledTimeInSec,
    maxTimeInSec,
    eventType,
    eventsPerspective,
  ]);
};
